import { Box, Grid, InputAdornment, TextField, Typography } from "@mui/material";
import { AutocompleteComponent } from "components/Autocomplete/AutocompleteComponent";
import { ModalV2 } from "components/Modal/ModalV2";
import React from "react";
import { VIN_LENGTH } from "helpers/dictionary";
import { onlyNumbers, onlyLetters, onlyVIN } from "helpers/regexFunctions";
import { useAdminUnitForm } from "./hooks";

export const AdminUnitsForm = ({
  data = {},
  setOpen = () => { },
  open = false,
  title = "Agregar unidad",
  edit = false,
}) => {
  const {
    values,
    setValues,
    handleSubmit,
    isLoadingComp,
    stationsList,
    unitTypes,
    creLicenses,
    errors,
    onChangeNumberV2,
    onChange,
    deliveryTypeOpt,
    fuelTypeOpt,
    capacityFieldDisabled,
    typeFieldDisabled,
  } = useAdminUnitForm(data, edit, setOpen);
  const {
    idStation,
    number,
    ecoPart1,
    ecoPart2,
    ecoPart3,
    type,
    deliveryType,
    fuel,
    capacity_,
    idealPercentage,
    idealPercentage_,
    flowMeter_,
    unitPlates,
    creLicense,
    brand,
    subModel,
    serieNumber,
  } = values;

  return (
    <ModalV2
      title={title}
      open={open}
      onClose={setOpen}
      onSubmit={handleSubmit}
      toRight
      isLoading={isLoadingComp?.create || isLoadingComp?.update}
    >
      <Grid item xs={12}>
        <AutocompleteComponent
          label="Planta"
          name="idStation"
          value={idStation}
          handleValues={setValues}
          fullWidth
          variant="outlined"
          textFieldProps={{
            error: !!errors.idStation,
            helperText: errors.idStation,
          }}
          options={stationsList}
          sort={true}
          disabled={edit}
        />
      </Grid>
      {!edit ? (
        <>
          <Grid item xs={12}>
            <Box p={2} mb={2} style={{ borderRadius: 4 }}>
              <Typography variant="subtitle2" gutterBottom>
                Estructura del Número Económico
              </Typography>
              <Typography variant="caption" display="block" gutterBottom>
                El número debe cumplir con el siguiente formato:
                <br />
                <b>CÓDIGO</b> (1-3 letras) - <b>NÚMERO</b> (1-3 dígitos) -{" "}
                <b>NÚMERO</b> (1-3 dígitos)
              </Typography>
              <Typography variant="caption" color="textSecondary">
                <i>Ejemplos válidos: ABC-000-000, A-000-000, AB-000-000</i>
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Código"
              placeholder="ABC"
              name="ecoPart1"
              value={ecoPart1}
              onChange={(e) =>
                setValues({ ecoPart1: onlyLetters(e.target.value, true) })
              }
              fullWidth
              variant="outlined"
              error={!!errors.ecoPart1}
              helperText={errors.ecoPart1}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Num 1"
              placeholder="000"
              name="ecoPart2"
              value={ecoPart2}
              onChange={(e) =>
                setValues({ ecoPart2: onlyNumbers(e.target.value) })
              }
              fullWidth
              variant="outlined"
              error={!!errors.ecoPart2}
              helperText={errors.ecoPart2}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Num 2"
              placeholder="000"
              name="ecoPart3"
              value={ecoPart3}
              onChange={(e) =>
                setValues({ ecoPart3: onlyNumbers(e.target.value) })
              }
              fullWidth
              variant="outlined"
              error={!!errors.ecoPart3}
              helperText={errors.ecoPart3}
            />
          </Grid>
        </>
      ) : (
        <Grid item xs={12}>
          <TextField
            label="Número económico"
            value={number}
            fullWidth
            variant="outlined"
            disabled
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <AutocompleteComponent
          label="Tipo de Unidad"
          name="type"
          value={type}
          handleValues={setValues}
          fullWidth
          variant="outlined"
          textFieldProps={{
            error: !!errors.type,
            helperText: errors.type,
          }}
          options={unitTypes}
          sort={true}
          disabled={typeFieldDisabled}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6}>
        <AutocompleteComponent
          label="Tipo de entrega"
          name="deliveryType"
          value={deliveryType}
          handleValues={setValues}
          fullWidth
          variant="outlined"
          textFieldProps={{
            error: !!errors.deliveryType,
            helperText: errors.deliveryType,
          }}
          options={deliveryTypeOpt}
          sort={true}
          disabled={typeFieldDisabled}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6}>
        <AutocompleteComponent
          label="Tipo de llenado"
          name="fuel"
          value={fuel}
          handleValues={setValues}
          fullWidth
          variant="outlined"
          textFieldProps={{
            error: !!errors.fuel,
            helperText: errors.fuel,
          }}
          options={fuelTypeOpt}
          sort={true}
          disabled={typeFieldDisabled}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          label="Capacidad"
          name="capacity"
          value={capacity_}
          onChange={(e) => onChangeNumberV2(e, { maxDigits: 10 })}
          fullWidth
          variant="outlined"
          error={!!errors.capacity}
          helperText={errors.capacity}
          InputProps={{
            endAdornment: (
              <InputAdornment style={{ marginLeft: 8 }} position="end">
                lts
              </InputAdornment>
            ),
            inputProps: { style: { textAlign: "right" } },
          }}
          disabled={capacityFieldDisabled}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          label="Cuenta Litros"
          name="flowMeter"
          value={flowMeter_}
          onChange={(e) => onChangeNumberV2(e, { maxDigits: 10 })}
          fullWidth
          variant="outlined"
          error={!!errors.flowMeter}
          helperText={errors.flowMeter}
          InputProps={{
            endAdornment: (
              <InputAdornment style={{ marginLeft: 8 }} position="end">
                lts
              </InputAdornment>
            ),
            inputProps: { style: { textAlign: "right" } },
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          label="Porcentaje ideal (Opcional)"
          name="idealPercentage"
          value={idealPercentage_}
          onChange={onChangeNumberV2}
          fullWidth
          variant="outlined"
          error={!!errors.idealPercentage}
          helperText={errors.idealPercentage}
          InputProps={{
            endAdornment: (
              <InputAdornment style={{ marginLeft: 8 }} position="end">
                %
              </InputAdornment>
            ),
            inputProps: { style: { textAlign: "right" } },
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <AutocompleteComponent
          label="Permiso CRE"
          name="creLicense"
          keyOption="creLicense"
          value={creLicense}
          handleValues={setValues}
          fullWidth
          variant="outlined"
          textFieldProps={{
            error: !!errors.creLicense,
            helperText: errors.creLicense,
          }}
          options={creLicenses?.filter((cre) => cre.id === idStation?.id)}
          sort={true}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          label="Placas"
          name="unitPlates"
          value={unitPlates}
          onChange={onChange}
          fullWidth
          variant="outlined"
          error={!!errors.unitPlates}
          helperText={errors.unitPlates}
          inputProps={{ maxLength: 10 }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          label="Marca"
          name="brand"
          value={brand}
          onChange={onChange}
          fullWidth
          variant="outlined"
          error={!!errors.brand}
          helperText={errors.brand}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          label="Submodelo"
          name="subModel"
          value={subModel}
          onChange={onChange}
          fullWidth
          variant="outlined"
          error={!!errors.subModel}
          helperText={errors.subModel}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label="Número de serie"
          name="serieNumber"
          value={serieNumber}
          onChange={(e) => setValues({ serieNumber: onlyVIN(e.target.value) })}
          fullWidth
          variant="outlined"
          error={!!errors.serieNumber}
          helperText={errors.serieNumber}
          inputProps={{ maxLength: VIN_LENGTH }}
        />
      </Grid>
    </ModalV2>
  );
};
